import {Injectable, inject} from '@angular/core';
import {
    ActionColumn,
    BaseCollectionModel,
    Pagination,
    SearchModel,
    TableSettings,
    YascMappedCrudControllerV3,
    YascOptionItem,
} from '@yasc/yasc-material-components';
import {BehaviorSubject, defer, firstValueFrom, from, map, shareReplay, tap} from 'rxjs';
import {RestApiService} from '../../services/rest-api.service';
import {RuntimeCacheService} from '../../services/runtime-cache.service';
import {UserController} from '../inventory/user.controller';
import {ProjectMapper} from '../../contracts/v0/crm/project.mapper';
import {PermissionList} from '../../permissions/permission-list';
import {Project} from '../../contracts/v0/crm/project.model';
import {ProjectRole, TrackerType} from '../../enums';
import {
    ProjectMinRes,
    ProjectSummaryAggregationRes,
    ProjectSummaryGroupingRes,
    ProjectTrackerRes,
    WorkflowDetailRes,
} from '../../contracts/v0/crm';

@Injectable({providedIn: 'root'})
export class ProjectController extends YascMappedCrudControllerV3<ProjectMapper> {
    private readonly rest = inject(RestApiService);
    private readonly runtimeCache = inject(RuntimeCacheService);
    private readonly userController = inject(UserController);

    private readonly customerOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);
    private readonly workflowOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);
    private readonly workflowStatusOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);

    readonly projectSearchLoaded$ = new BehaviorSubject<boolean>(false);

    projects: ProjectMinRes[] = [];
    workflows: WorkflowDetailRes[] = [];
    workflowOptions: YascOptionItem<number>[] = [];
    customers: YascOptionItem<number>[] = [];

    private readonly projectTeamUsers$ = defer(() => from(this.userController.loadData())).pipe(
        map(users => users.data.map(user => new YascOptionItem<number>(user.userName, user.id))),
        shareReplay(1),
    );

    override readonly mapper = new ProjectMapper();
    override readonly api = this.rest.v0.crm.project;

    override readonly fieldOptions = {
        projectCustomers: {
            options$: this.customerOptions$.asObservable(),
            translateNames: false,
            allowNonOptions: true,
        },
        projectWorkflows: {
            options$: this.workflowOptions$.asObservable(),
            translateNames: false,
            allowNonOptions: true,
        },
        projectWorkflowStatuses: {
            options$: this.workflowStatusOptions$.asObservable(),
            translateNames: false,
            allowNonOptions: true,
        },
        projectTeamUsers: {
            options$: this.projectTeamUsers$,
            translateNames: false,
            allowNonOptions: true,
        },
        projectRoles: {
            options: Object.keys(ProjectRole)
                .filter(key => Number.isNaN(Number(key)))
                .map(name => new YascOptionItem<number>(name, ProjectRole[name as keyof typeof ProjectRole] as number)),
            translateNames: false,
            allowNonOptions: true,
        },
    };

    override readonly showUndo = true;
    override readonly showRedo = true;
    override readonly showReset = true;
    override readonly undoStackSize = 50;

    override readonly tableSettings = new TableSettings<Project>({
        yascRowClick: false,
        yascInlineEditing: false,
        yascShowFilters: true,
        yascShowIndexCol: true,
        yascLabel: 'general_project',
        yascShowColumnsMenu: true,
        yascResizeColumns: true,
        yascMultiSelect: true,
        yascReorderColumns: true,
        yascGroupingHeaders: true,
        yascActionColumns: [
            ActionColumn.Show,
            {action: ActionColumn.Edit, displayIf: () => this.runtimeCache.hasPermission(PermissionList.ProjectUpdate)},
            {action: ActionColumn.Copy, displayIf: () => this.runtimeCache.hasPermission(PermissionList.ProjectCreate)},
            {action: ActionColumn.Delete, displayIf: () => this.runtimeCache.hasPermission(PermissionList.ProjectDelete)},
        ],
    });

    override readonly actionDisplay = {
        show: () => this.runtimeCache.hasPermission(PermissionList.ProjectDetails),
        create: () => this.runtimeCache.hasPermission(PermissionList.ProjectCreate),
        edit: () => this.runtimeCache.hasPermission(PermissionList.ProjectUpdate),
        copy: () => this.runtimeCache.hasPermission(PermissionList.ProjectCreate),
        delete: () => this.runtimeCache.hasPermission(PermissionList.ProjectDelete),
    };

    constructor() {
        super();
        this.loadProjectOptions();
    }

    override loadDetail(row: Project) {
        return this.api.detail(row.id).pipe(
            map(response => {
                const customer = response.customer ?? row.customer;
                this.ensureCustomerOption(customer);

                return this.mapper.fromDetailResponse({
                    ...response,
                    customer,
                    workflowId: response.workflowId ?? row.workflowId,
                    currentWorkflowStatus: response.currentWorkflowStatus ?? row.currentWorkflowStatus,
                } as ProjectTrackerRes);
            }),
        );
    }

    async loadData(searchModel?: SearchModel): Promise<BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>> {
        const model = searchModel ?? new SearchModel({
            sorting: [],
            filters: [],
            pagination: new Pagination({
                index: 0,
                size: 150,
            }),
        });

        try {
            const result = await firstValueFrom(this.rest.v0.crm.project.searchWithSummary(model));
            this.projects = result.data ?? [];
            this.projectSearchLoaded$.next(true);
            this.applyGroupingOptions(result.summary?.groupings);
            return result;
        } catch (error) {
            this.projectSearchLoaded$.next(false);
            throw error;
        }
    }

    private loadProjectOptions(): void {
        this.rest.v0.company.search().subscribe({
            next: result => {
                this.customers = result.data.map(company => new YascOptionItem<number>(company.name, company.id));
                this.customerOptions$.next(this.customers);
            },
            error: () => {
                this.customers = [];
                this.customerOptions$.next([]);
            },
        });

        this.rest.v0.crm.workflow.search().pipe(
            map(result => result.data.filter(workflow => workflow.type === TrackerType.Project)),
            tap(workflows => this.updateWorkflowOptions(workflows)),
        ).subscribe({
            error: () => {
                this.workflows = [];
                this.workflowOptions = [];
                this.workflowOptions$.next([]);
                this.workflowStatusOptions$.next([]);
            },
        });
    }

    private updateWorkflowOptions(workflows: WorkflowDetailRes[]): void {
        this.workflows = workflows;
        this.workflowOptions = workflows.map(workflow => new YascOptionItem<number>(workflow.name, workflow.id));
        this.workflowOptions$.next(this.workflowOptions);

        this.workflowStatusOptions$.next(
            workflows
                .flatMap(workflow => workflow.states ?? [])
                .filter((status, index, all) => all.findIndex(x => x.id === status.id) === index)
                .map(status => new YascOptionItem<number>(status.name, status.id)),
        );
    }

    private ensureCustomerOption(customer?: {id: number; name: string} | null): void {
        if (!customer || this.customers.some(option => option.value === customer.id)) return;

        this.customers = [new YascOptionItem<number>(customer.name, customer.id), ...this.customers];
        this.customerOptions$.next(this.customers);
    }

    private applyGroupingOptions(grouping?: ProjectSummaryGroupingRes): void {
        if (!grouping) return;

        if (grouping.availableCompanies?.length) {
            this.customers = grouping.availableCompanies.map(company => new YascOptionItem<number>(company.name, company.id));
            this.customerOptions$.next(this.customers);
        }

        if (grouping.availableWorkflows?.length) {
            this.workflowStatusOptions$.next(
                grouping.availableWorkflows.map(status => new YascOptionItem<number>(status.name, status.id)),
            );
        }
    }
}
